import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle, ArrowRightLeft, Briefcase, Calendar, Coffee, Lock, LucideIcon, Pencil, Play, Plus, Square, Trash2, X,
} from 'lucide-react';
import {
  addPortalHourly,
  deletePortalHourly,
  getPortalTimesheetStatus,
  updatePortalHourly,
} from '../../../api/portal';
import { allocateHundredths, jobCode, jobTitle, isOverhead, minutesToHundredths } from './portalModel';
import { overlapMessage } from './portalErrors';
import { fmtHours, fmtTime, localMinutes, localYmd } from './portalTime';
import { newClientPunchID } from './punchStore';
import SrcChip from './SrcChip';
import JobPicker from './JobPicker';
import {
  buildManualRowBody,
  buildRowEditBody,
  DAY_PUNCH_LABEL,
  DayPunch,
  DayPunchKind,
  dayPunches,
  DayTotals,
  DOW_LONG,
  dowIndex,
  DOW_SHORT,
  LOCK_EXPORTED_MESSAGE,
  LOCK_SUBMITTED_MESSAGE,
  moveRowToOtherWeek,
  OUT_BEFORE_IN_MESSAGE,
  parseTimeInput,
  planMealInsert,
  planPunchEdit,
  precheckPeriod,
  rowsOnDay,
  toTimeInput,
  TsRow,
  wallToInstant,
  defaultAddRange,
  findOverlap,
  weekLabel,
  WeekMoveError,
} from './timesheetModel';
import {
  PortalJob,
  PortalPayPeriod,
  PUNCH_TYPE_MANUAL_ADD,
  TYPE_MEAL,
  TYPE_REGULAR,
} from './portalTypes';

/**
 * My Timesheet — one day (handoff §4, screenshots 28–30). The day's punches with their
 * source and Edited markers; in an open week each can be edited inline (PUT the whole row,
 * punch type 3 on the moved side — both rows for a meal / switch boundary), a forgotten
 * clock out can be added (punch type 2), missing work time or a missing meal added (POST,
 * punch type 2, with a clientPunchID), and a time block deleted after a confirm. An edit
 * that would carry a punch into another week is pre-checked here and offered as delete +
 * re-add instead of hitting PAY_PERIOD_CHANGE. The body is a fixed 470px (D7): editors and
 * confirms replace rows in place.
 */

const DAY_START_MIN = 6 * 60;
const DAY_SPAN_MIN = 14 * 60;

const KIND_ICON: Record<DayPunchKind, LucideIcon> = {
  in: Play,
  out: Square,
  'meal-start': Coffee,
  'meal-end': Coffee,
  'meal2-start': Coffee,
  'meal2-end': Coffee,
  switch: ArrowRightLeft,
  missingOut: AlertTriangle,
};

function kindClass(kind: DayPunchKind): string {
  if (kind === 'in') return 'in';
  if (kind === 'out') return 'out';
  if (kind === 'switch') return 'sw';
  if (kind === 'missingOut') return 'miss';
  return 'meal';
}

function longDate(ymd: string): string {
  const d = new Date(`${ymd}T00:00:00Z`);
  return `${DOW_LONG[dowIndex(ymd)]}, ${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })}`;
}

export interface DayDetailProps {
  ymd: string;
  rows: TsRow[];
  period: PortalPayPeriod;
  periods: PortalPayPeriod[];
  zone: string;
  todayYmd: string;
  nowMs: number;
  totals: DayTotals | null;
  lock: 'submitted' | 'exported' | null;
  /** The week's status didn't load: show the punches, offer no edits (the lock state is unknown). */
  statusUnknown?: boolean;
  /** The role job-costs (portalJobMode): "Add missing time" offers the job picker. */
  jobCosting: boolean;
  /** The non-job charge (portalJobMode.nonJob): every manual work time when the role doesn't job-cost. */
  defaultJob: PortalJob;
  /** A 401 inside the job picker. */
  onSessionExpired: () => void;
  employeeID: number;
  onClose: () => void;
  /** After a successful write: reload the week, toast `message`. */
  onChanged: (message: string) => Promise<void>;
  /** Classifies a failed write (session modal, lock reloads) and returns the message to show. */
  describeError: (err: unknown) => string;
}

interface EditState {
  key: string;
  date: string;
  time: string;
  msg: string;
  tone: 'hint' | 'err' | 'warn';
  /** A pre-checked move into another week, offered as delete + re-add. */
  move: { rowID: number; newInMs: number; label: string; target: PortalPayPeriod } | null;
}

interface AddState {
  kind: 'work' | 'meal';
  from: string;
  to: string;
  msg: string;
  tone: 'hint' | 'err';
}

const DayDetailModal: React.FC<DayDetailProps> = (props) => {
  const { ymd, rows, period, periods, zone, todayYmd, nowMs, totals, lock, jobCosting, defaultJob, employeeID } = props;
  const editable = !lock && !props.statusUnknown;
  const [edit, setEdit] = useState<EditState | null>(null);
  const [add, setAdd] = useState<AddState | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [deleteErr, setDeleteErr] = useState<string | null>(null);

  // The week locked under us (a refusal reloaded it): close every editor.
  useEffect(() => {
    if (!lock) return;
    setEdit(null);
    setAdd(null);
    setConfirmDelete(null);
  }, [lock]);

  const byID = useMemo(() => new Map(rows.map((r) => [r.id, r])), [rows]);
  const punches = useMemo(() => dayPunches(rows, ymd, zone, todayYmd), [rows, ymd, zone, todayYmd]);
  const dayRows = useMemo(() => rowsOnDay(rows, ymd, zone), [rows, ymd, zone]);
  const lastRegJob = [...dayRows].reverse().find((r) => r.type === TYPE_REGULAR)?.job ?? null;
  // Job costing per role: the picker's choice (default: the day's last job), else always non-job time.
  const [pickedJob, setPickedJob] = useState<PortalJob | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const addJob = jobCosting ? pickedJob ?? lastRegJob ?? defaultJob : defaultJob;
  const idle = !edit && !add && confirmDelete == null && !busy;

  // Work blocks' hours, allocated (largest remainder) to add up to the header's "h paid";
  // meal blocks keep their own minutes. See allocateHundredths.
  const blockHundredths = useMemo(() => {
    const work = dayRows.filter((r) => r.type !== TYPE_MEAL);
    const mins = work.map((r) => Math.max(0, ((r.outMs ?? Math.max(nowMs, r.inMs)) - r.inMs) / 60000));
    const target = totals && work.every((r) => r.outMs != null) ? minutesToHundredths(totals.total) : undefined;
    const parts = allocateHundredths(mins, target);
    return new Map(work.map((r, k) => [r.id, parts[k]] as [number, number]));
  }, [dayRows, nowMs, totals]);

  // An editor that opens low in the scroll area is brought into view (no jump when it already shows).
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const addOpen = !!add;
  useEffect(() => {
    if (!edit?.key && !addOpen) return;
    const el = bodyRef.current?.querySelector('.dp-row.editing');
    if (el && typeof (el as HTMLElement).scrollIntoView === 'function') (el as HTMLElement).scrollIntoView({ block: 'nearest' });
  }, [edit?.key, addOpen]);

  const startEdit = (p: DayPunch) => {
    const at = p.kind === 'missingOut' ? Math.max(p.atMs, Math.min(nowMs, p.atMs + 8 * 3600000)) : p.atMs;
    setAdd(null);
    setConfirmDelete(null);
    setEdit({
      key: p.key,
      date: localYmd(at, zone),
      time: toTimeInput(at, zone),
      msg: p.kind === 'missingOut' ? 'Enter when you stopped working' : 'Saved as Edited · your supervisor sees the change when you submit',
      tone: 'hint',
      move: null,
    });
  };

  const fail = (patch: Partial<EditState>) => setEdit((e) => (e ? { ...e, ...patch } : e));

  const saveEdit = async (p: DayPunch) => {
    if (!edit) return;
    const minutes = parseTimeInput(edit.time);
    if (minutes == null || !/^\d{4}-\d{2}-\d{2}$/.test(edit.date)) {
      fail({ msg: 'Enter a date and a time.', tone: 'err', move: null });
      return;
    }
    const newMs = wallToInstant(edit.date, minutes, zone);
    if (p.kind === 'missingOut') {
      const row = byID.get(p.sides[0].rowID);
      if (!row) return;
      if (newMs <= row.inMs) {
        fail({ msg: OUT_BEFORE_IN_MESSAGE, tone: 'err' });
        return;
      }
      await run(async () => {
        await updatePortalHourly(row.id, buildRowEditBody(row.raw, { outMs: newMs }, zone, PUNCH_TYPE_MANUAL_ADD));
      }, 'Clock out added — it shows as Edited');
      return;
    }
    const plan = planPunchEdit(p, newMs, rows);
    if (!plan.ok) {
      fail({ msg: plan.reason, tone: 'err', move: null });
      return;
    }
    // Pre-check the week: the API files a row under the LOCAL date of its in-time.
    for (const put of plan.puts) {
      if (put.change.inMs === undefined) continue;
      const check = precheckPeriod(put.change.inMs, zone, period, periods);
      if (check.kind === 'moves') {
        const row = byID.get(put.rowID);
        const where = check.target ? `the week of ${weekLabel(check.target)}` : 'another week';
        if (p.sides.length === 1 && row && row.outMs != null && check.target) {
          fail({
            msg: `That date is in ${where}. A punch can't move between weeks — delete this block and add it again there.`,
            tone: 'warn',
            move: { rowID: row.id, newInMs: put.change.inMs, label: where, target: check.target },
          });
        } else {
          fail({ msg: `That date is in ${where}. Punches can't move between weeks — delete them and add them in that week.`, tone: 'err', move: null });
        }
        return;
      }
    }
    await run(async () => {
      let done = 0;
      try {
        for (const put of plan.puts) {
          const row = byID.get(put.rowID);
          if (!row) continue;
          await updatePortalHourly(row.id, buildRowEditBody(row.raw, put.change, zone));
          done += 1;
        }
      } catch (err) {
        if (done > 0) (err as { partial?: boolean }).partial = true;
        throw err;
      }
    }, 'Punch saved — it shows as Edited');
  };

  /**
   * Re-add in the other week, then delete the original (moveRowToOtherWeek): the target
   * week's status is checked first, the new row is written before the original goes, and
   * a failure part-way undoes what was written.
   */
  const moveToOtherWeek = async (move: NonNullable<EditState['move']>) => {
    const row = byID.get(move.rowID);
    if (!row || row.outMs == null) return;
    await run(async () => {
      try {
        await moveRowToOtherWeek(
          { getStatus: getPortalTimesheetStatus, add: addPortalHourly, update: updatePortalHourly, remove: deletePortalHourly, newClientPunchID },
          { employeeID, zone, row, newInMs: move.newInMs, target: move.target },
        );
      } catch (err) {
        if (!(err instanceof WeekMoveError)) throw err;
        if (err.refusedUpFront) throw Object.assign(new Error(err.message), { userMessage: err.message });
        const why = props.describeError(err.cause);
        const userMessage = err.restored
          ? `${why} Nothing was moved — your original punch is unchanged.`
          : `${why} The move stopped part-way and couldn't be fully undone — check both weeks.`;
        throw Object.assign(new Error(userMessage), { userMessage });
      }
    }, `Moved to ${move.label}`);
  };

  const startAdd = () => {
    setEdit(null);
    setConfirmDelete(null);
    setPickedJob(null);
    // A free gap on the day (defaultAddRange), so the first save isn't refused as an overlap.
    setAdd({ kind: 'work', ...defaultAddRange(dayRows, ymd, zone, nowMs, todayYmd), msg: '', tone: 'hint' });
  };

  const saveAdd = async () => {
    if (!add) return;
    const from = parseTimeInput(add.from);
    const to = parseTimeInput(add.to);
    if (from == null || to == null) {
      setAdd({ ...add, msg: 'Enter a start and an end time.', tone: 'err' });
      return;
    }
    if (to <= from) {
      setAdd({ ...add, msg: 'The end time must be after the start time (same day).', tone: 'err' });
      return;
    }
    const inMs = wallToInstant(ymd, from, zone);
    const outMs = wallToInstant(ymd, to, zone);
    if (add.kind === 'work') {
      // The API refuses PUNCH_OVERLAP; say which punch here, before any write.
      const clash = findOverlap(dayRows, inMs, outMs, nowMs);
      if (clash) {
        setAdd({
          ...add,
          msg: overlapMessage(fmtTime(clash.inMs, zone), clash.outMs != null ? fmtTime(clash.outMs, zone) : null, clash.type === TYPE_MEAL),
          tone: 'err',
        });
        return;
      }
      await run(async () => {
        await addPortalHourly({
          ...buildManualRowBody({ employeeID, inMs, outMs, zone, type: TYPE_REGULAR, job: addJob }),
          clientPunchID: newClientPunchID(),
        });
      }, 'Time added — it shows as Edited');
      return;
    }
    const plan = planMealInsert(dayRows, inMs, outMs);
    if ('reason' in plan) {
      setAdd({ ...add, msg: plan.reason, tone: 'err' });
      return;
    }
    const host = plan.row;
    await run(async () => {
      // 1) end the worked block at the meal start, 2) add the meal, 3) add the rest of the shift.
      await updatePortalHourly(host.id, buildRowEditBody(host.raw, { outMs: inMs }, zone, PUNCH_TYPE_MANUAL_ADD));
      let mealID: number | null = null;
      try {
        const meal = await addPortalHourly({
          ...buildManualRowBody({ employeeID, inMs, outMs, zone, type: TYPE_MEAL, job: host.job }),
          clientPunchID: newClientPunchID(),
        });
        mealID = meal.timeEntryHourlyID ?? null;
        await addPortalHourly({
          ...buildManualRowBody({ employeeID, inMs: outMs, outMs: host.outMs, zone, type: TYPE_REGULAR, job: host.job, punchTypeOut: host.raw.punchTypeOut }),
          clientPunchID: newClientPunchID(),
        });
      } catch (err) {
        // Undo (best effort): drop the meal row, then give the block its original out back.
        try {
          if (mealID != null) await deletePortalHourly(mealID);
          await updatePortalHourly(host.id, buildRowEditBody(host.raw, {}, zone));
        } catch { /* reported below */ }
        throw err;
      }
    }, 'Meal added — it shows as Edited');
  };

  const doDelete = async (rowID: number) => {
    await run(async () => { await deletePortalHourly(rowID); }, 'Time block deleted');
  };

  /** Runs a write; on success closes the editors and lets the page reload; on failure shows why where the user is. */
  async function run(write: () => Promise<void>, success: string) {
    setBusy(true);
    setDeleteErr(null);
    try {
      await write();
      setEdit(null);
      setAdd(null);
      setConfirmDelete(null);
      await props.onChanged(success);
    } catch (err) {
      const partial = !!(err as { partial?: boolean })?.partial;
      // A message already written for the user (a week move) is shown as is.
      const userMessage = (err as { userMessage?: unknown })?.userMessage;
      const msg = typeof userMessage === 'string'
        ? userMessage
        : props.describeError(err) + (partial ? ' Part of the change was saved — check the day.' : '');
      setEdit((e) => (e ? { ...e, msg, tone: 'err', move: null } : e));
      setAdd((a) => (a ? { ...a, msg, tone: 'err' } : a));
      setDeleteErr(msg);
    } finally {
      setBusy(false);
    }
  }

  /* ------------------------------------------------ render pieces */

  const timeLabel = (ms: number) => {
    const d = localYmd(ms, zone);
    return d === ymd ? fmtTime(ms, zone) : `${fmtTime(ms, zone)} (${DOW_SHORT[dowIndex(d)]})`;
  };

  const pct = (ms: number) => {
    const base = localYmd(ms, zone) > ymd ? 24 * 60 : 0;
    return Math.max(0, Math.min(100, ((base + localMinutes(ms, zone) - DAY_START_MIN) / DAY_SPAN_MIN) * 100));
  };

  const punchRow = (p: DayPunch) => {
    const Icon = KIND_ICON[p.kind];
    if (edit?.key === p.key) {
      return (
        <div key={p.key} className="dp-row editing">
          <div className="dp-ed">
            <span className={`lab dp-k ${kindClass(p.kind)}`}><Icon />{DAY_PUNCH_LABEL[p.kind]}</span>
            <input className="input mono" type="date" aria-label="Date" value={edit.date} disabled={busy}
              onChange={(e) => setEdit({ ...edit, date: e.target.value, move: null, tone: 'hint' })} />
            <input className="input mono" type="time" aria-label="Time" value={edit.time} disabled={busy}
              onChange={(e) => setEdit({ ...edit, time: e.target.value, move: null, tone: 'hint' })} />
            <span />
            <button type="button" className="btn sm primary" disabled={busy} onClick={() => void saveEdit(p)}>{busy ? 'Saving…' : 'Save'}</button>
            <button type="button" className="btn sm" disabled={busy} onClick={() => setEdit(null)}>Cancel</button>
          </div>
          <div className={`dp-msg ${edit.tone === 'hint' ? '' : edit.tone}`} role={edit.tone === 'hint' ? undefined : 'alert'}>
            <span>{edit.msg}</span>
            {edit.move && (
              <button type="button" className="btn sm" disabled={busy} onClick={() => void moveToOtherWeek(edit.move!)}>Delete &amp; re-add</button>
            )}
          </div>
        </div>
      );
    }
    const missing = p.kind === 'missingOut';
    return (
      <div key={p.key} className="dp-row">
        <span className={`dp-k ${kindClass(p.kind)}`}><Icon />{DAY_PUNCH_LABEL[p.kind]}</span>
        <span className="dp-t">{missing ? 'Missing' : timeLabel(p.atMs)}</span>
        <span className="dp-x">
          {missing
            ? <span className="sub">Never clocked out — add the time you stopped</span>
            : <SrcChip source={p.source} short />}
          {p.edited && <span className="edited">Edited</span>}
          {p.kind === 'switch' && p.from && p.to && <span className="sub mono">{jobCode(p.from)} → {jobCode(p.to)}</span>}
        </span>
        {editable && (
          <span className="dp-acts">
            <button type="button" className="btn sm ghost" disabled={!idle} onClick={() => startEdit(p)}>
              {missing ? <><Plus />Add</> : <><Pencil />Edit</>}
            </button>
          </span>
        )}
      </div>
    );
  };

  const blockRow = (r: TsRow) => {
    const hundredths = blockHundredths.get(r.id);
    const meal = r.type === TYPE_MEAL;
    const end = r.outMs ?? Math.max(nowMs, r.inMs);
    const range = `${fmtTime(r.inMs, zone)} – ${r.outMs != null ? timeLabel(r.outMs) : 'now'}`;
    if (confirmDelete === r.id) {
      return (
        <div key={`b-${r.id}`} className="dp-row confirm">
          <span className="dp-k out"><Trash2 />Delete?</span>
          <span className="dp-x">
            {deleteErr
              ? <span className="sub" role="alert" style={{ color: '#a8331f', fontWeight: 600 }}>{deleteErr}</span>
              : <span className="sub">{meal ? 'Meal' : 'Work'} {range} is removed from your timesheet.</span>}
          </span>
          <span className="dp-acts">
            <button type="button" className="btn sm danger" disabled={busy} onClick={() => void doDelete(r.id)}>{busy ? 'Deleting…' : 'Delete'}</button>
            <button type="button" className="btn sm" disabled={busy} onClick={() => { setConfirmDelete(null); setDeleteErr(null); }}>Keep</button>
          </span>
        </div>
      );
    }
    return (
      <div key={`b-${r.id}`} className="dp-row">
        <span className={`dp-k ${meal ? 'meal' : 'in'}`}>{meal ? <Coffee /> : <Play />}{meal ? 'Meal' : 'Work'}</span>
        <span className="dp-t">{hundredths != null ? (hundredths / 100).toFixed(2) : fmtHours((end - r.inMs) / 60000)} h</span>
        <span className="dp-x">
          <span className="sub mono">{range}</span>
          {jobCosting && !meal && <span className="sub">{isOverhead(r.job) ? jobTitle(r.job) : `${jobTitle(r.job)} · ${jobCode(r.job)}`}</span>}
        </span>
        {editable && (
          <span className="dp-acts">
            <button type="button" className="iconbtn del" aria-label="Delete this time block" disabled={!idle}
              onClick={() => { setEdit(null); setAdd(null); setConfirmDelete(r.id); }}>
              <Trash2 />
            </button>
          </span>
        )}
      </div>
    );
  };

  const addRow = add && (
    <div className="dp-row editing">
      <div className="dp-ed add">
        <select className="select" aria-label="What to add" value={add.kind} disabled={busy}
          onChange={(e) => setAdd({ ...add, kind: e.target.value as AddState['kind'], msg: '', tone: 'hint' })}>
          <option value="work">Work time</option>
          <option value="meal">Meal break</option>
        </select>
        <input className="input mono" type="time" aria-label="From" value={add.from} disabled={busy}
          onChange={(e) => setAdd({ ...add, from: e.target.value, msg: '', tone: 'hint' })} />
        <input className="input mono" type="time" aria-label="To" value={add.to} disabled={busy}
          onChange={(e) => setAdd({ ...add, to: e.target.value, msg: '', tone: 'hint' })} />
        {jobCosting && add.kind === 'work' ? (
          <button type="button" className="btn sm add-job" disabled={busy} title="Charge this time to…" onClick={() => setPickerOpen(true)}>
            <Briefcase /><span>{jobTitle(addJob)}</span>
          </button>
        ) : <span />}
        <button type="button" className="btn sm primary" disabled={busy} onClick={() => void saveAdd()}>{busy ? 'Saving…' : 'Save'}</button>
        <button type="button" className="btn sm" disabled={busy} onClick={() => setAdd(null)}>Cancel</button>
      </div>
      <div className={`dp-msg ${add.tone === 'err' ? 'err' : ''}`} role={add.tone === 'err' ? 'alert' : undefined}>
        <span>
          {add.msg || (add.kind === 'work'
            ? `Added as a manual punch on ${isOverhead(addJob) ? jobTitle(addJob) : `${jobTitle(addJob)} · ${jobCode(addJob)}`}`
            : 'Splits your worked time around the meal')}
        </span>
      </div>
    </div>
  );

  const paid = totals?.total ?? 0;
  const ot = (totals?.ot ?? 0) + (totals?.dt ?? 0);
  const canAdd = editable && ymd <= todayYmd;

  return (
    <div className="etp-modal-bg" role="presentation" onMouseDown={(e) => { if (e.target === e.currentTarget && !busy && !pickerOpen) props.onClose(); }}>
      <div className="etp-modal day" role="dialog" aria-modal="true" aria-labelledby="etp-day-title">
        <div className="modal-head">
          <div className="mi"><Calendar /></div>
          <div>
            <h3 id="etp-day-title">{longDate(ymd)}</h3>
            <div className="ms">
              {fmtHours(paid)} h paid{ot ? ` · ${fmtHours(ot)} h overtime` : ''}{dayRows.some((r) => r.outMs == null) ? ' · in progress' : ''}
            </div>
          </div>
          <button type="button" className="x" aria-label="Close" onClick={props.onClose} disabled={busy}><X /></button>
        </div>
        <div className="modal-body" ref={bodyRef}>
          <div className="day-tl">
            <div className="tlwrap">
              <div className="tl">
                {dayRows.map((r) => {
                  const left = pct(r.inMs);
                  const width = Math.max(0, pct(r.outMs ?? Math.max(nowMs, r.inMs)) - left);
                  const cls = r.type === TYPE_MEAL ? 'meal' : r.outMs == null ? 'live' : 'work';
                  return <div key={`tl-${r.id}`} className={`tl-seg ${cls}`} style={{ left: `${left}%`, width: `${width}%` }} />;
                })}
              </div>
              <div className="tl-ax">{['6 AM', '9 AM', '12 PM', '3 PM', '6 PM', '8 PM'].map((t) => <span key={t}>{t}</span>)}</div>
            </div>
          </div>

          <div className="mini-lab">Punches</div>
          <div className="dp-list">
            {punches.length === 0 && <div className="pk-empty">No punches on this day</div>}
            {punches.map(punchRow)}
          </div>

          <div className="dp-sec">
            <span className="mini-lab">Time blocks</span>
            <span className="grow" />
            {canAdd && (
              <button type="button" className="linkbtn" disabled={!idle} onClick={startAdd}><Plus />Add missing time</button>
            )}
          </div>
          <div className="dp-list">
            {dayRows.length === 0 && !add && <div className="pk-empty">Nothing recorded</div>}
            {dayRows.map(blockRow)}
            {addRow}
          </div>

          {lock && (
            <div className="notice lock"><Lock /><span className="grow">{lock === 'exported' ? LOCK_EXPORTED_MESSAGE : LOCK_SUBMITTED_MESSAGE}</span></div>
          )}
        </div>
        <div className="modal-foot">
          <span className="grow mfoot-note">{editable ? 'Edits show as Edited and are logged to your activity' : ''}</span>
          <button type="button" className="btn" onClick={props.onClose} disabled={busy}>Close</button>
        </div>
      </div>
      {pickerOpen && jobCosting && (
        <JobPicker
          currentJob={addJob}
          overhead={defaultJob}
          employeeID={employeeID}
          title="What was this time for?"
          subLine={`Charges the time you add on ${longDate(ymd)}`}
          overheadLabel={jobTitle(defaultJob)}
          overheadSub="Not job related · office time"
          onPick={(j) => { setPickedJob(j); setPickerOpen(false); }}
          onClose={() => setPickerOpen(false)}
          onSessionExpired={() => { setPickerOpen(false); props.onSessionExpired(); }}
        />
      )}
    </div>
  );
};

export default DayDetailModal;
